import { cfetch, BadStatusError } from '@confluence/network';

import {
	isValidSnippetMetadataResponse,
	type SnippetMetadataPayload,
	type SnippetMetadataResponse,
} from './types/conversationalAiClientTypes.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
	'X-Experience-Id': 'confluence-snippets-metadata',
	'X-Product': 'confluence',
} as const;

const CONVERSATIONAL_AI_PLATFORM_API_URL = '/gateway/api/assist';
const METADATA_PATH = '/api/confluence/v1/snippets/metadata';

export class SnippetMetadataError extends Error {
	statusCode: number | undefined;

	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'SnippetMetadataError';
		this.statusCode = statusCode;
	}
}

/**
 * Fetches AI-generated name and description suggestions for a snippet from the Conversational AI
 * Platform metadata endpoint. Caller must pass a SnippetMetadataPayload (e.g. { adfContent: JSON.stringify(adf) })
 * and cloudId from session data for the request to succeed.
 */
export const fetchSnippetMetadata = async (
	snippetMetadataPayload: SnippetMetadataPayload,
	cloudId: string,
): Promise<SnippetMetadataResponse> => {
	let response: Response;
	try {
		response = await cfetch(`${CONVERSATIONAL_AI_PLATFORM_API_URL}${METADATA_PATH}`, {
			method: 'POST',
			headers: {
				...COMMON_HEADERS,
				'X-Cloudid': cloudId,
			},
			body: JSON.stringify(snippetMetadataPayload),
		});
	} catch (err) {
		if (BadStatusError.isBadStatusError(err)) {
			throw new SnippetMetadataError(
				`Snippet metadata request failed: ${err.response.statusText}`,
				err.response.status,
			);
		}
		throw err;
	}

	const data: SnippetMetadataResponse = await response.json();
	if (!isValidSnippetMetadataResponse(data)) {
		throw new SnippetMetadataError('Snippet metadata request failed: Invalid response');
	}
	return data;
};
